import { Breadcrumb, HeaderToolbar, Skeleton } from "@xola/ui-kit";
import clsx from "clsx";
import { isUndefined } from "lodash";
import React, { ReactNode, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Merchandise } from "types";
import { Page } from "../Page";
import { useMerchandiseTabs } from "./useMerchandiseTabs";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { SidebarTabs } from "@/components/Common/SidebarTabs";
import { ColumnLayout } from "@/components/Layout/ColumnLayout";
import { useProduct } from "@/hooks/resources/useProduct";

export const MerchandisePage = () => {
    const history = useHistory();
    const { id, section } = useParams<{ id?: string; section?: string }>();

    const { product: merchandise } = useProduct<Merchandise>(id);

    const tabs = useMerchandiseTabs(merchandise);
    const initialSection = section ?? tabs[0].id;
    const initialActiveIndex = tabs.findIndex((tab) => tab.id === (section ?? initialSection));
    const [activeTabIndex, setActiveTabIndex] = useState(initialActiveIndex === -1 ? 0 : initialActiveIndex);

    useEffect(() => {
        if (section) {
            const newIndex = tabs.findIndex((tab) => tab.id === section);
            if (newIndex >= 0 && activeTabIndex !== newIndex) {
                setActiveTabIndex(newIndex);
            }
        }
    }, [section, tabs, activeTabIndex]);

    const breadcrumbs: Array<{ id: string; label: ReactNode; url?: string; tabIndex?: number }> = [
        { id: "products", label: "Products", url: "/products/merchandise" },
        { id: "merchandise", label: "Merchandise", url: "/products/merchandise" },
        { id: "product-name", label: merchandise ? <NoTranslate>{merchandise.name}</NoTranslate> : "New", tabIndex: 1 },
        { id: "active-tab", label: tabs[activeTabIndex].label, tabIndex: activeTabIndex },
    ];

    const handleBreadcrumbClick = (index: number, url?: string) => {
        if (url) {
            history.push(url);
        } else {
            handleActiveTabChange(index);
        }
    };

    const handleActiveTabChange = (index: number) => {
        if (id) {
            const path = `/products/merchandise/${id}/${tabs[index].id}`;
            history.push(path);
        } else {
            setActiveTabIndex(index);
        }
    };

    return (
        <Page isDynamicTitle={!!merchandise} title={merchandise ? merchandise.name : "Create Merchandise"}>
            <ColumnLayout>
                <HeaderToolbar classNames="justify-between">
                    <HeaderToolbar.Breadcrumb className="flex">
                        {breadcrumbs.map((breadcrumb) => (
                            <Breadcrumb.Item
                                key={breadcrumb.id}
                                className="!inline-block max-w-24 truncate lg:max-w-50 2xl:max-w-150"
                                onClick={() => handleBreadcrumbClick(breadcrumb.tabIndex ?? 1, breadcrumb.url)}
                            >
                                {breadcrumb.label}
                            </Breadcrumb.Item>
                        ))}
                    </HeaderToolbar.Breadcrumb>
                    <div id="header-toolbar-actions" />
                </HeaderToolbar>

                <ColumnLayout.Fill>
                    <SidebarTabs activeTab={activeTabIndex} onClick={handleActiveTabChange}>
                        {tabs.map((tab) => {
                            const isHidden = !isUndefined(tab.condition) && !tab.condition;
                            return (
                                <SidebarTabs.Tab
                                    key={tab.id}
                                    name={tab.label}
                                    icon={tab.icon}
                                    description={tab.description}
                                    className={clsx(isHidden && "hidden")}
                                >
                                    {tab.content ?? (
                                        <div className="p-4">
                                            <Skeleton />
                                        </div>
                                    )}
                                </SidebarTabs.Tab>
                            );
                        })}
                    </SidebarTabs>
                </ColumnLayout.Fill>
            </ColumnLayout>
        </Page>
    );
};
